import type { DocumentExtension, DocumentValueDto } from '@app/contracts';
import { parseStoredSnapshotV1 } from '@app/contracts';

const SWOT_SERIAL_KEYS = ['Forces', 'Faiblesses', 'Opportunités', 'Menaces'] as const;
const PESTEL_SERIAL_KEYS = [
  'Politique',
  'Economique',
  'Social',
  'Technologique',
  'Environnemental',
  'Legal',
] as const;

function matrixItemHighlightKeys(
  subSer: unknown,
  baseSer: unknown,
  keys: readonly string[],
): Set<string> {
  const out = new Set<string>();
  const subObj = subSer && typeof subSer === 'object' ? (subSer as Record<string, unknown>) : {};
  const baseObj =
    baseSer && typeof baseSer === 'object' ? (baseSer as Record<string, unknown>) : {};
  for (const k of keys) {
    const sa = Array.isArray(subObj[k]) ? (subObj[k] as unknown[]) : [];
    const ba = Array.isArray(baseObj[k]) ? (baseObj[k] as unknown[]) : [];
    const bm = new Map<string, unknown>();
    for (const it of ba) {
      if (it && typeof it === 'object' && 'clientKey' in it) {
        const ck = String((it as { clientKey: unknown }).clientKey);
        if (ck) bm.set(ck, it);
      }
    }
    for (const it of sa) {
      if (!it || typeof it !== 'object' || !('clientKey' in it)) continue;
      const ck = String((it as { clientKey: unknown }).clientKey);
      if (!ck) continue;
      const prev = bm.get(ck);
      if (prev === undefined || JSON.stringify(prev) !== JSON.stringify(it)) {
        out.add(`${k}:${ck}`);
      }
    }
  }
  return out;
}

/**
 * Met en évidence les blocs modifiés par rapport au cliché de la dernière publication (si parsable en v1).
 */
export function computeValidationHighlights(
  extension: DocumentExtension,
  displayValues: DocumentValueDto[],
  baselineSnapshot: unknown,
): {
  matrixItemKeys: Set<string>;
  changedValueIds: Set<string>;
} {
  const matrixItemKeys = new Set<string>();
  const changedValueIds = new Set<string>();
  const base = parseStoredSnapshotV1(baselineSnapshot);

  if (extension === 'swot') {
    const v = displayValues[0];
    if (v) {
      const bp = base?.values.find((x) => x.valueId === v.id);
      for (const k of matrixItemHighlightKeys(
        v.serialization,
        bp?.serialization,
        SWOT_SERIAL_KEYS,
      )) {
        matrixItemKeys.add(k);
      }
    }
    return { matrixItemKeys, changedValueIds };
  }

  if (extension === 'pestel') {
    const v = displayValues[0];
    if (v) {
      const bp = base?.values.find((x) => x.valueId === v.id);
      for (const k of matrixItemHighlightKeys(
        v.serialization,
        bp?.serialization,
        PESTEL_SERIAL_KEYS,
      )) {
        matrixItemKeys.add(k);
      }
    }
    return { matrixItemKeys, changedValueIds };
  }

  for (const v of displayValues) {
    const bp = base?.values.find((x) => x.valueId === v.id);
    const sub = JSON.stringify(v.serialization ?? null);
    const bas = JSON.stringify(bp?.serialization ?? null);
    if (sub !== bas) changedValueIds.add(v.id);
  }

  return { matrixItemKeys, changedValueIds };
}
